/**
 * Conference edition of the BetterStep onboarding.
 *
 * Every question, option label and score below is copied verbatim from the
 * production app so the booth demo asks what the real product asks:
 *   BetterStep/src/shared/config/addiction-categories.json  (areas, ranks, options)
 *   BetterStep/src/features/auth/redesignUserProfile/detailQuestions.ts (screener)
 *   BetterStep/src/features/auth/redesignUserProfile/barc10Questions.ts (BARC-10)
 *   BetterStep/src/shared/i18n/locales/en.ts  (headers and copy)
 *
 * MINIFIED FOR THE BOOTH — what the real flow has and this deliberately drops:
 *   - the seven profile steps (name, gender, body, location, ethnicity, work,
 *     living); only age survives, because BetterStep Age needs it
 *   - per-area subtypes and the search field (ten areas fit on one screen)
 *   - the exact quit date, replaced by five buckets (QUIT_BUCKETS)
 *   - BARC-10's two context questions (the third is auto-derived anyway)
 *   - the permission asks and the celebration screen
 */

// ── Areas ─────────────────────────────────────────────────────────────────
// clinicalSeverityRank + lifeImpactBase are the production values; the second
// drives prioritisation (see lib/priority.js).

export const AREA_GROUPS = [
  { id: 'substances', label: 'SUBSTANCES' },
  { id: 'behavioral', label: 'BEHAVIORAL' },
]

export const AREAS = [
  { slug: 'alcohol', group: 'substances', label: 'Alcohol', hint: 'beer, wine, spirits', icon: 'Wine', copyGroup: 'substance', lifeImpactBase: 9, verb: 'drink alcohol', noun: 'alcohol' },
  { slug: 'opioids', group: 'substances', label: 'Opioids & Painkillers', hint: 'heroin, oxy, codeine', icon: 'Pill', copyGroup: 'substance', lifeImpactBase: 10, verb: 'use opioids', noun: 'opioids' },
  { slug: 'stimulants', group: 'substances', label: 'Stimulants', hint: 'cocaine, meth, adderall', icon: 'Zap', copyGroup: 'substance', lifeImpactBase: 10, verb: 'use stimulants', noun: 'stimulants' },
  { slug: 'depressants', group: 'substances', label: 'Depressants & Sedatives', hint: 'xanax, benzos, ambien', icon: 'Tablets', copyGroup: 'substance', lifeImpactBase: 10, verb: 'use depressants', noun: 'depressants' },
  { slug: 'cannabis', group: 'substances', label: 'Cannabis', hint: 'flower, edibles, carts', icon: 'Leaf', copyGroup: 'substance', lifeImpactBase: 6, verb: 'use cannabis', noun: 'cannabis' },
  { slug: 'psychedelics', group: 'substances', label: 'Psychedelics', hint: 'LSD, shrooms, ketamine', icon: 'Sparkle', copyGroup: 'substance', lifeImpactBase: 4, verb: 'use psychedelics', noun: 'psychedelics' },
  { slug: 'nicotine', group: 'substances', label: 'Nicotine', hint: 'cigarettes, vaping', icon: 'Cigarette', copyGroup: 'substance', lifeImpactBase: 3, verb: 'use nicotine', noun: 'nicotine' },
  { slug: 'gambling', group: 'behavioral', label: 'Gambling', hint: 'betting, casino, online', icon: 'Dices', copyGroup: 'abstinence-behavioral', lifeImpactBase: 8, verb: 'gamble', noun: 'gambling' },
  { slug: 'pornography', group: 'behavioral', label: 'Pornography', hint: 'video, webcam, images', icon: 'EyeOff', copyGroup: 'abstinence-behavioral', lifeImpactBase: 4, verb: 'view pornography', noun: 'pornography' },
  { slug: 'gaming-hardcore', group: 'behavioral', label: 'Gaming', hint: 'online, ranked, loot boxes', icon: 'Gamepad2', copyGroup: 'abstinence-behavioral', lifeImpactBase: 4, verb: 'game excessively', noun: 'gaming' },
]

export const AREA_BY_SLUG = Object.fromEntries(AREAS.map((a) => [a.slug, a]))

// ── Recovery gate ─────────────────────────────────────────────────────────

export const COPY_GROUPS = {
  substance: { activeLabel: 'Currently using', recoveryLabel: 'In recovery' },
  'abstinence-behavioral': { activeLabel: 'Currently engaging', recoveryLabel: 'In recovery' },
}

/** `value` is the production multiplier the priority formula reads. */
export const FREQUENCY_OPTIONS = [
  { label: 'Daily', value: 1.30 },
  { label: 'Few times a week', value: 1.15 },
  { label: 'Weekly', value: 1.05 },
  { label: 'Monthly or less', value: 1.00 },
]

export const CONTROL_QUESTION = 'Can you stop when you want to?'

export const CONTROL_OPTIONS = [
  { label: 'No', value: 1.30 },
  { label: "It's hard", value: 1.15 },
  { label: 'Usually', value: 1.05 },
  { label: 'Yes', value: 1.00 },
]

/**
 * Booth substitute for the production date picker. `days` is the midpoint used
 * for the recency multiplier; the label is what the attendee actually picks.
 */
export const QUIT_BUCKETS = [
  { label: 'In the last week', days: 3 },
  { label: 'A few weeks ago', days: 21 },
  { label: '1–3 months ago', days: 60 },
  { label: '3–12 months ago', days: 200 },
  { label: 'Over a year ago', days: 500 },
]

// ── Screener (production wording, scores 4 → 0) ───────────────────────────

const SCREENER_TEMPLATE = [
  {
    q: (a) => `When did you last ${a.verb}?`,
    o: [
      ['Today', 4],
      ['In the past week', 3],
      ['In the past month', 2],
      ['In the past year', 1],
      ['Over a year ago', 0],
    ],
  },
  {
    q: (a) => `How often do you ${a.verb}?`,
    o: [
      ['Daily or almost daily', 4],
      ['Several times a week', 3],
      ['Once or twice a week', 2],
      ['A few times a month', 1],
      ['Rarely or never', 0],
    ],
  },
  {
    q: (a) => `When you ${a.verb}, how much is typical?`,
    o: [
      ['Significantly more than intended', 4],
      ['More than intended', 3],
      ['About what I plan', 2],
      ['Less than I used to', 1],
      ['Very little / controlled', 0],
    ],
  },
  {
    q: (a) => `How difficult is it to control your ${a.noun} use?`,
    o: [
      ['Impossible to control', 4],
      ['Very difficult', 3],
      ['Somewhat difficult', 2],
      ['Slightly difficult', 1],
      ['Not difficult at all', 0],
    ],
  },
  {
    q: (a) => `How much has ${a.noun} impacted your daily life?`,
    o: [
      ['Severely - major areas affected', 4],
      ['Significantly - several areas affected', 3],
      ['Moderately - some areas affected', 2],
      ['Mildly - minor effects', 1],
      ['Not at all', 0],
    ],
  },
  {
    q: (a) => `How strong are your cravings for ${a.noun}?`,
    o: [
      ['Overwhelming - hard to think about anything else', 4],
      ['Strong - frequently on my mind', 3],
      ['Moderate - noticeable but manageable', 2],
      ['Mild - occasional thoughts', 1],
      ['None - rarely think about it', 0],
    ],
  },
]

/** The six screener questions for one area. Max score 24. */
export function buildScreener(slug) {
  const area = AREA_BY_SLUG[slug]
  if (!area) return []
  return SCREENER_TEMPLATE.map((entry, i) => ({
    index: i,
    question: entry.q(area),
    answers: entry.o.map(([text, score]) => ({ text, score })),
  }))
}

export const SCREENER_MAX = 24

// ── BARC-10 (recovery capital) ────────────────────────────────────────────

export const BARC10_LIKERT = [
  'Strongly Disagree',
  'Disagree',
  'Slightly Disagree',
  'Neutral',
  'Slightly Agree',
  'Agree',
  'Strongly Agree',
]

/** `{{addiction}}` is replaced with the primary area's noun at render time. */
export const BARC10_QUESTIONS = [
  { id: 'barc_01', text: 'There are more important things to me in life than {{addiction}}' },
  { id: 'barc_02', text: 'In general, I am happy with my life' },
  { id: 'barc_03', text: 'I have enough energy to complete everyday tasks' },
  { id: 'barc_04', text: 'I am proud of my community and feel part of it' },
  { id: 'barc_05', text: 'I get lots of support from friends' },
  { id: 'barc_06', text: 'I regard my life as challenging and fulfilling without {{addiction}}' },
  { id: 'barc_07', text: 'My living space has helped to drive my recovery journey' },
  { id: 'barc_08', text: 'I take full responsibility for my actions' },
  { id: 'barc_09', text: 'I am happy dealing with a range of professional people' },
  { id: 'barc_10', text: 'I am making good progress on my recovery journey' },
]

export const BARC10_MAX = BARC10_QUESTIONS.length * 6

// ── Headers (production copy) ─────────────────────────────────────────────

export const HEADERS = {
  areas: 'What brings you here?',
  recoveryGate: 'Where are you now?',
  prioritization: 'Which is your main focus?',
  screener: 'A few quick questions',
  barc10: 'Your recovery capital',
}

export const OVERRIDE_WARNING = {
  title: "Changing this isn't recommended",
  body: (label) =>
    `Based on your answers, ${label} is the most pressing area to focus on first, that's why we set it as your main focus. Switching to a lower-priority one isn't recommended: your plan works best when it targets your highest-priority area first. You can still continue if you're sure.`,
}
